import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import {
  LayoutDashboard,
  Network,
  Clock,
  Users,
  Calculator,
  Wallet,
  Package,
  Cpu,
  Wrench,
  FolderKanban,
  TrendingUp,
  MapPin,
  Route,
  Settings,
  LogOut,
  ChevronLeft,
  Megaphone,
  Map,
} from 'lucide-react';

export const menuItems = [
  { id: 'orgchart', label: 'iSource Organization Chart', icon: Network },
  { id: 'announcements', label: 'Announcements', icon: Megaphone },
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'mapview', label: 'Map View', icon: Map },
  { id: 'clockora', label: 'Clockora', icon: Clock },
  { id: 'humacore', label: 'HumaCore', icon: Users },
  { id: 'numora', label: 'Numora', icon: Calculator },
  { id: 'payora', label: 'Payora', icon: Wallet },
  { id: 'inventora', label: 'Inventora', icon: Package },
  { id: 'pcc', label: 'PCC', icon: Cpu },
  { id: 'servicore', label: 'ServiCore', icon: Wrench },
  { id: 'projectora', label: 'Projectora', icon: FolderKanban },
  { id: 'salesora', label: 'Salesora', icon: TrendingUp },
  { id: 'mappoint', label: 'MapPoint', icon: MapPin },
  { id: 'routecore', label: 'RouteCore', icon: Route },
  { id: 'settings', label: 'Settings', icon: Settings },
];

export const MODULE_SUBMENUS = {
  orgchart: [
    { id: 'overview', label: 'Overview' },
    { id: 'tree', label: 'Organization Tree' },
    { id: 'positions', label: 'Positions' },
    { id: 'departments', label: 'Departments' },
  ],
  announcements: [
    { id: 'overview', label: 'Overview' },
    { id: 'compose', label: 'Compose' },
    { id: 'archive', label: 'Archive' },
  ],
  dashboard: [
    { id: 'overview', label: 'Overview' },
    { id: 'analytics', label: 'Analytics' },
    { id: 'reports', label: 'Reports' },
  ],
  mapview: [
    { id: 'map', label: 'Map' },
    { id: 'locations', label: 'Locations' },
    { id: 'geofence', label: 'Geofence' },
  ],
  clockora: [
    { id: 'attendance', label: 'Attendance' },
    { id: 'timesheet', label: 'Timesheet' },
    { id: 'breaks', label: 'Breaks' },
    { id: 'leaves', label: 'Leave Requests' },
  ],
  humacore: [
    { id: 'overview', label: 'Overview' },
    { id: 'employees', label: 'Employees' },
    { id: 'recruitment', label: 'Recruitment' },
    { id: 'performance', label: 'Performance' },
  ],
  numora: [
    { id: 'overview', label: 'Overview' },
    { id: 'accounts', label: 'Accounts' },
    { id: 'transactions', label: 'Transactions' },
    { id: 'reports', label: 'Reports' },
  ],
  payora: [
    { id: 'overview', label: 'Overview' },
    { id: 'payroll', label: 'Payroll Run' },
    { id: 'employees', label: 'Employees' },
    { id: 'reports', label: 'Reports' },
  ],
  inventora: [
    { id: 'overview', label: 'Overview' },
    { id: 'stocks', label: 'Stocks' },
    { id: 'inbound', label: 'Inbound' },
    { id: 'outbound', label: 'Outbound' },
  ],
  pcc: [
    { id: 'details', label: 'Project Details' },
    { id: 'computation', label: 'Project Computation' },
  ],
  servicore: [
    { id: 'dashboard', label: 'Overview' },
    { id: 'fsr', label: 'Field Service Report' },
    { id: 'quotation', label: 'Quotation Request' },
    { id: 'mif', label: 'Machine in Field' },
  ],
  projectora: [
    { id: 'overview', label: 'Overview' },
    { id: 'projects', label: 'Project Management' },
  ],
  salesora: [
    { id: 'project-details', label: 'Overview' },
    { id: 'project-details-map', label: 'MAP' },
    { id: 'project-details-info', label: 'Sales Performance Report' },
    { id: 'project-details-blueprint', label: 'Blue Print' },
    { id: 'project-details-focus-products', label: 'Focus Products' },
  ],
  mappoint: [
    { id: 'overview', label: 'Overview' },
    { id: 'points', label: 'Map Points' },
    { id: 'routes', label: 'Routes' },
  ],
  routecore: [
    { id: 'overview', label: 'Overview' },
    { id: 'planning', label: 'Route Planning' },
    { id: 'dispatch', label: 'Dispatch' },
    { id: 'tracking', label: 'Live Tracking' },
  ],
  settings: [
    { id: 'users', label: 'Users & Access' },
    { id: 'enroll', label: 'Enroll User' },
    { id: 'system', label: 'System Config' },
  ],
};

export default function Sidebar({
  activeMenu,
  onMenuSelect,
  collapsed,
  onToggle,
  user,
  onLogout,
  axiosInstance,
}) {
  const [logoutVisible, setLogoutVisible] = useState(false);
  const hideTimerRef = useRef(null);
  const isHoveringBtnRef = useRef(false);
  const isHoveringCardRef = useRef(false);
  const [pendingApprovalsCount, setPendingApprovalsCount] = useState(0);
  const [exportReadyCount, setExportReadyCount] = useState(0);

  const effectiveModuleAccess = user?.module_access || {};
  const userEmail = (user?.email || '').toLowerCase();
  const SETTINGS_OWNER_EMAILS = new Set(['vinsimon23@gmail.com', 'vinsimon@gmail.com'].map(s => s.toLowerCase()));
  const isSettingsOwner = SETTINGS_OWNER_EMAILS.has(userEmail);

  const isDgmUser =
    userEmail === 'dgm@isourceasia.com' ||
    (user?.full_name || '').toUpperCase() === 'DONALD G. MADINO';
  const isCadfUser = userEmail === 'cadf@isourceasiaworkplace.com';
  const isOamUser = userEmail === 'oam@isourceasiaworkplace.com';
  const isJcpUser = userEmail === 'jcp@isourceasia.com';
  const isMspUser = userEmail === 'msp@isourceasia.com.ph';
  const isPccApprovalUser = isDgmUser || isCadfUser || isOamUser || isJcpUser || isMspUser;
  const pccEntry = effectiveModuleAccess.pcc || {};
  const pccVisibleBySettings = !!pccEntry.visible;
  const hasPccAccess =
    pccVisibleBySettings && (!!user?.permissions?.ccp_access || SETTINGS_OWNER_EMAILS.has(userEmail));

  useEffect(() => {
    if (!hasPccAccess || !axiosInstance || !isPccApprovalUser && !isOamUser) return;

    let mounted = true;
    const fetchCounts = async () => {
      try {
        const res = await axiosInstance.get('/ccp/project-details');
        const details = Array.isArray(res.data) ? res.data : [];

        let pending = 0;
        let exportReady = 0;

        details.forEach((p) => {
          const isPlaceholder = p.project_type === 'Account_Placeholder';
          if (isPlaceholder) return;

          const isApproved = p.is_approved === true || p.is_approved === 'true' || p.is_approved === 1;
          const isApprovedPricing =
            p.is_approved_pricing === true || p.is_approved_pricing === 'true' || p.is_approved_pricing === 1;
          const isApprovedCosts =
            p.is_approved_costs === true || p.is_approved_costs === 'true' || p.is_approved_costs === 1;
          const isApprovedJoygie =
            p.is_approved_joygie === true || p.is_approved_joygie === 'true' || p.is_approved_joygie === 1;
          const isApprovedOverall =
            p.is_approved_overall === true || p.is_approved_overall === 'true' || p.is_approved_overall === 1;

          if (isDgmUser && !isApproved) pending++;
          else if (isCadfUser && isApproved && !isApprovedPricing) pending++;
          else if (isOamUser && isApprovedPricing && !isApprovedCosts) pending++;
          else if (isJcpUser && isApprovedCosts && !isApprovedJoygie) pending++;
          else if (isMspUser && isApprovedJoygie && !isApprovedOverall) pending++;

          if (isOamUser && isApprovedOverall) {
            const isExported =
              p.is_exported_by_oam === true ||
              p.is_exported_by_oam === 'true' ||
              p.is_exported_by_oam === 1;
            if (!isExported) exportReady++;
          }
        });

        if (mounted) {
          setPendingApprovalsCount(pending);
          setExportReadyCount(exportReady);
        }
      } catch (err) {
        console.warn('[Sidebar] Failed to fetch PCC counts:', err?.message || err);
      }
    };

    fetchCounts();
    const interval = setInterval(fetchCounts, 30000);

    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [
    axiosInstance,
    hasPccAccess,
    isPccApprovalUser,
    isDgmUser,
    isCadfUser,
    isOamUser,
    isJcpUser,
    isMspUser,
  ]);

  const visibleMenuItems = useMemo(() => menuItems.filter((item) => {
    const entry = effectiveModuleAccess[item.id];
    if (item.id === 'settings') {
      // Settings visibility is strictly owner-emails-only. No bypass for non-owners,
      // even super admins.
      return isSettingsOwner;
    }
    // Strictly obey module_access.visible for every user / every module.
    // If the doc has no explicit entry, fall back to the 4-default employee set
    // only (new enroll produces explicit False.
    if (entry) return !!entry.visible;
    const userRole = user?.role || 'employee';
    const defaultVisible =
      userRole !== 'employee'
        ? true
        : ['dashboard', 'mapview', 'clockora', 'announcements'].includes(item.id);
    return defaultVisible;
  }), [effectiveModuleAccess, isSettingsOwner, user?.role]);

  const visibleMenuItemsWithBadges = useMemo(
    () =>
      visibleMenuItems.map((item) => {
        if (item.id !== 'pcc') return item;
        const badgeBlue = isPccApprovalUser && pendingApprovalsCount > 0 ? pendingApprovalsCount : null;
        const badgeGreen = isOamUser && exportReadyCount > 0 ? exportReadyCount : null;
        return { ...item, badgeBlue, badgeGreen };
      }),
    [visibleMenuItems, isPccApprovalUser, pendingApprovalsCount, isOamUser, exportReadyCount]
  );

  const clearHideTimer = useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const scheduleHide = useCallback(() => {
    clearHideTimer();
    hideTimerRef.current = setTimeout(() => {
      if (!isHoveringBtnRef.current && !isHoveringCardRef.current) {
        setLogoutVisible(false);
      }
    }, 1000);
  }, [clearHideTimer]);

  const handleCardEnter = useCallback(() => {
    isHoveringCardRef.current = true;
    clearHideTimer();
    setLogoutVisible(true);
  }, [clearHideTimer]);

  const handleCardLeave = useCallback(() => {
    isHoveringCardRef.current = false;
    scheduleHide();
  }, [scheduleHide]);

  const handleBtnEnter = useCallback(() => {
    isHoveringBtnRef.current = true;
    clearHideTimer();
    setLogoutVisible(true);
  }, [clearHideTimer]);

  const handleBtnLeave = useCallback(() => {
    isHoveringBtnRef.current = false;
    if (!isHoveringCardRef.current) {
      scheduleHide();
    }
  }, [scheduleHide]);

  const lbl = `side-label ${collapsed ? 'is-closed' : 'is-open'}`;

  const displayName = user?.full_name || user?.name || 'Juan Dela Cruz';
  const displayRole = user?.is_super_admin
    ? 'Super Administrator'
    : user?.role === 'admin'
    ? 'System Administrator'
    : user?.role === 'employee'
    ? 'Employee'
    : user?.role || 'System Administrator';
  const initials = displayName
    .split(' ')
    .map((n) => n[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase() || 'JD';

  return (
    <div
      className={`glass-panel-transparent sidebar-panel relative rounded-2xl flex flex-col h-full ${
        collapsed ? 'w-[78px]' : 'w-[260px]'
      }`}
    >
      <button
        onClick={onToggle}
        className="btn-3d absolute -right-3 top-6 z-30 w-6 h-6 rounded-full border backdrop-blur-md flex items-center justify-center shadow-lg shadow-black/20"
        style={{
          background: 'var(--nad-s3)',
          borderColor: 'var(--nad-b2)',
          color: 'var(--nad-t2)',
        }}
        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        onMouseEnter={(e) => {
          e.currentTarget.style.color = 'var(--nad-accent-txt)';
          e.currentTarget.style.borderColor = 'var(--nad-accent-txt)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = 'var(--nad-t2)';
          e.currentTarget.style.borderColor = 'var(--nad-b2)';
        }}
      >
        <ChevronLeft
          size={13}
          strokeWidth={2.5}
          style={{
            transform: collapsed ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 900ms cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        />
      </button>

      <div className="flex items-center gap-3 px-[19px] pt-6 pb-4 border-b" style={{ borderColor: 'var(--nad-b1)' }}>
        <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center flex-shrink-0 shadow-lg shadow-black/10 p-1.5">
          <img src="/iSource LOGO1.png" alt="iSource" className="w-full h-full object-contain" />
        </div>
        <div className={`${lbl} overflow-hidden`}>
          <h1 className="text-sm font-bold tracking-wide whitespace-nowrap" style={{ color: 'var(--nad-t1)' }}>
            iSource Suite
          </h1>
          <p className="text-[10px] tracking-wider uppercase whitespace-nowrap" style={{ color: 'var(--nad-t3)' }}>
            Business Solutions
          </p>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto overflow-x-hidden py-4 px-3 space-y-1.5">
        <p
          className={`${lbl} text-[10px] font-semibold uppercase tracking-widest px-3 mb-3 overflow-hidden`}
          style={{
            color: 'var(--nad-t4)',
            height: collapsed ? 0 : 'auto',
            marginBottom: collapsed ? 0 : undefined,
          }}
        >
          Navigation
        </p>

        {visibleMenuItemsWithBadges.map((item) => {
          const isActive = activeMenu === item.id;
          const Icon = item.icon;
          return (
            <button
              key={item.id}
              onClick={() => onMenuSelect(item.id)}
              className={`menu-item-glow menu-3d ${isActive ? 'is-active' : ''}
                relative w-full flex items-center gap-3 rounded-xl px-3 py-2.5
              `}
              title={item.label}
              style={{
                color: isActive ? 'var(--nad-accent-txt)' : 'var(--nad-t3)',
                background: isActive
                  ? 'linear-gradient(to right, rgba(250,204,21,0.15), rgba(59,130,246,0.10))'
                  : undefined,
              }}
              onMouseEnter={(e) => {
                if (!isActive) e.currentTarget.style.background = 'var(--nad-s1)';
              }}
              onMouseLeave={(e) => {
                if (!isActive) e.currentTarget.style.background = 'transparent';
              }}
            >
              {isActive && <div className="menu-active-indicator" />}

              <span className="menu-ico flex w-7 items-center justify-center flex-shrink-0">
                <Icon size={20} />
              </span>

              <span
                className={`${lbl} menu-txt text-[13px] font-medium flex-1 text-left overflow-hidden text-ellipsis`}
                style={{ color: isActive ? 'var(--nad-t1)' : 'inherit' }}
              >
                {item.label}
              </span>

              <div className={`${lbl} flex items-center gap-1.5`}>
                {item.badgeBlue && (
                  <span
                    className="menu-txt text-[10px] font-bold px-2 py-0.5 rounded-full overflow-hidden"
                    style={{
                      background: 'rgba(37,99,235,0.90)',
                      color: '#ffffff',
                      boxShadow: '0 1px 2px rgba(15,23,42,0.15), 0 0 0 1px rgba(255,255,255,0.08) inset',
                    }}
                  >
                    {item.badgeBlue}
                  </span>
                )}
                {item.badgeGreen && (
                  <span
                    className="menu-txt text-[10px] font-bold px-2 py-0.5 rounded-full overflow-hidden"
                    style={{
                      background: 'rgba(16,185,129,0.95)',
                      color: '#ffffff',
                      boxShadow: '0 1px 2px rgba(15,23,42,0.15), 0 0 0 1px rgba(255,255,255,0.08) inset',
                    }}
                  >
                    {item.badgeGreen}
                  </span>
                )}
                {!item.badgeBlue && !item.badgeGreen && item.badge && (
                  <span
                    className="menu-txt text-[10px] font-bold px-2 py-0.5 rounded-full overflow-hidden"
                    style={{
                      background: isActive ? 'rgba(250,204,21,0.20)' : 'var(--nad-s2)',
                      color: isActive ? 'var(--nad-accent-txt)' : 'var(--nad-t3)',
                    }}
                  >
                    {item.badge}
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </nav>

      <div className="px-3 pb-4 border-t pt-3" style={{ borderColor: 'var(--nad-b1)' }}>
        <div className="relative">
          <div
            className={`absolute left-0 right-0 -top-11 z-50 transition-all duration-300 ${
              logoutVisible
                ? 'opacity-100 translate-y-0 pointer-events-auto'
                : 'opacity-0 translate-y-1 pointer-events-none'
            }`}
            onMouseEnter={handleBtnEnter}
            onMouseLeave={handleBtnLeave}
          >
            <button
              onClick={onLogout}
              className="btn-3d w-full flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-xs font-medium backdrop-blur-sm"
              style={{
                background: 'rgba(248,113,113,0.15)',
                border: '1px solid rgba(248,113,113,0.30)',
                color: 'var(--nad-neg)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(248,113,113,0.25)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(248,113,113,0.15)';
              }}
            >
              <LogOut size={14} className="flex-shrink-0" />
              <span className={lbl}>Log Out</span>
            </button>
          </div>

          <div
            className="user-3d flex items-center gap-3 rounded-xl p-2.5 cursor-pointer"
            style={{ background: 'var(--nad-s1)' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--nad-s2)';
              handleCardEnter();
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'var(--nad-s1)';
              handleCardLeave();
            }}
          >
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-brand-500 to-brand-600 flex items-center justify-center flex-shrink-0 ring-2 ring-brand-400/20">
              <span className="text-white font-bold text-sm">{initials}</span>
            </div>
            <div className={`${lbl} flex-1 min-w-0 overflow-hidden`}>
              <p className="text-[13px] font-semibold truncate" style={{ color: 'var(--nad-t1)' }}>{displayName}</p>
              <p
                className="text-[10px] font-medium whitespace-nowrap"
                style={{ color: 'var(--nad-accent-soft)' }}
              >
                {displayRole}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
